import React, { useState, useEffect } from "react";
import { Info, AlertCircle } from "lucide-react";

const AuthenticationBody = ({ template, setTemplate }) => {
  const [codeDelivery, setCodeDelivery] = useState("zero-tap");
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(true);
  const [addExpiryTime, setAddExpiryTime] = useState(false);
  const [zeroTapAgreement, setZeroTapAgreement] = useState(false);

  const [apps, setApps] = useState([{ id: 1, packageName: "", signatureHash: "" }]);

  const addApp = () => {
    if (apps.length < 5) {
      setApps([...apps, { id: Date.now(), packageName: "", signatureHash: "" }]);
    }
  };

  const removeApp = (id) => {
    if (apps.length > 1) {
      setApps(apps.filter((app) => app.id !== id));
    }
  };

  const showAutofillButton = () =>
    codeDelivery === "zero-tap" || codeDelivery === "one-tap";

  useEffect(() => {
    const newTemplate = {
      ...template,
      components: [
        {
          type: "body",
          add_security_recommendation: addSecurityRecommendation,
        },
        ...(addExpiryTime
          ? [
              {
                type: "FOOTER",
                code_expiration_minutes: 10,
              },
            ]
          : []),
        {
          type: "BUTTONS",
          buttons: [
            {
              type: "otp",
              otp_type:
                codeDelivery === "copy"
                  ? "copy_code"
                  : codeDelivery === "zero-tap"
                  ? "zero_tap"
                  : "one_tap",
              text:
                codeDelivery === "copy"
                  ? "Copy Code"
                  : codeDelivery === "zero-tap"
                  ? "Auto-Fill"
                  : "One-Tap",
              autofill_text:
                codeDelivery !== "copy" ? "Use this code automatically" : undefined,
              zero_tap_terms_accepted: codeDelivery === "zero-tap" ? zeroTapAgreement : undefined,
              supported_apps:
                showAutofillButton() && apps.length > 0
                  ? apps.map((app) => ({
                      package_name: app.packageName,
                      signature_hash: app.signatureHash,
                    }))
                  : undefined,
            },
          ],
        },
      ],
    };

    setTemplate(newTemplate);
  }, [
    codeDelivery,
    addSecurityRecommendation,
    addExpiryTime,
    zeroTapAgreement,
    apps,
  ]);

  return (
    <div className="w-full bg-gray-50">
      <div className="space-y-6 mb-4">
        {/* Code Delivery Setup */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Code delivery setup</h2>
            <p className="text-sm text-gray-600 mb-6">
              Choose how customers send the code from WhatsApp to your app. Edits to this section won't require review or count towards edit limits. Learn how to send{" "}
              <a href="#" className="text-blue-600 hover:underline">authentication message templates</a>.
            </p>
            
            <div className="space-y-4">
              {/* Zero-tap auto-fill */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="codeDelivery"
                  checked={codeDelivery === "zero-tap"}
                  onChange={() => setCodeDelivery("zero-tap")}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Zero-tap auto-fill</span>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    This is recommended as the easiest option for your customers. Zero-tap will automatically send code without requiring your customer to tap a button. An auto-fill or copy code message will be sent if zero-tap and auto-fill aren't possible.
                  </p>
                </div>
              </label>

              {/* One-tap auto-fill */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="codeDelivery"
                  checked={codeDelivery === "one-tap"}
                  onChange={() => setCodeDelivery("one-tap")}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">One-tap auto-fill</span>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    The code sends to your app when customers tap the button. A copy code message will be sent if auto-fill isn't possible.
                  </p>
                </div>
              </label>

              {/* Copy code */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="codeDelivery"
                  checked={codeDelivery === "copy"}
                  onChange={() => setCodeDelivery("copy")}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Copy code</span>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Basic authentication with quick setup. Your customers copy and paste the code into your app.
                  </p>
                </div>
              </label>

              {/* Zero-tap agreement */}
              {codeDelivery === "zero-tap" && (
                <div className="mt-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={zeroTapAgreement}
                        onChange={(e) => setZeroTapAgreement(e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                      />
                      <div className="text-sm flex-1">
                        <p className="text-gray-700 leading-relaxed">
                          By selecting zero-tap, I understand that Test WhatsApp Business Account's use of zero-tap authentication is subject to the{" "}
                          <a href="#" className="text-blue-600 hover:underline">WhatsApp Business Terms of Service</a>
                          . It's Test WhatsApp Business Account's responsibility to ensure that its customers expect that the code will be automatically filled in on their behalf when they choose to receive the zero-tap code through WhatsApp.
                        </p>
                        <div className="mt-3">
                          <a href="#" className="text-blue-600 hover:underline font-medium">
                            Learn more and review best practices.
                          </a>
                        </div>
                      </div>
                    </label>
                    
                    {!zeroTapAgreement && (
                      <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-200">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <p className="text-sm text-red-700">This box must be ticked to submit this template.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* App Setup */}
        {showAutofillButton() && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">App setup</h2>
              <p className="text-sm text-gray-600 mb-6">You can add up to 5 apps.</p>
              
              <div className="space-y-4 w-full">
                {apps.map((app, index) => (
                  <div key={app.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full">
                      {/* Package Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Package name
                        </label>
                        <input
                          type="text"
                          value={app.packageName}
                          onChange={(e) => {
                            const updated = [...apps];
                            updated[index].packageName = e.target.value;
                            setApps(updated);
                          }}
                          placeholder="Package name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>

                      {/* App signature hash */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          App signature hash
                        </label>
                        <input
                          type="text"
                          value={app.signatureHash}
                          onChange={(e) => {
                            const updated = [...apps];
                            updated[index].signatureHash = e.target.value;
                            setApps(updated);
                          }}
                          placeholder="Signature hash"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>

                    {/* Remove button */}
                    {apps.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeApp(app.id)}
                        className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove app
                      </button>
                    )}
                  </div>
                ))}

                {/* Add another app button */}
                {apps.length < 5 && (
                  <button
                    type="button"
                    onClick={addApp}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    Add another app
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Content</h2>
            <p className="text-sm text-gray-600 mb-6">
              Content for authentication message templates can't be edited. You can add additional content from the options below.
            </p>
            
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addSecurityRecommendation}
                  onChange={(e) => setAddSecurityRecommendation(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">Add security recommendation</span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addExpiryTime}
                  onChange={(e) => setAddExpiryTime(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Add expiry time for the code</span>
                  <p className="text-xs text-gray-600 mt-1">
                    After the code has expired, the auto-fill button will be disabled.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationBody;